/**
 * DunningTriggerDialog — Startet eine Zahlungsaufforderung für GENAU ein
 * Mietverhältnis mit Vorschau des erkannten Szenarios (Miete + Kaution).
 *
 * Aufruf: TenancyDetail-Menü „Zahlungsaufforderung erstellen".
 * Backend: GET /dunning/analyze/{tenancy_id} + POST /dunning/run-tenancy/{tenancy_id}.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatEUR, formatDate } from "@/lib/constants";
import { Info, Warning, CheckCircle, Receipt, HandCoins } from "@phosphor-icons/react";

const SCENARIO_LABEL = {
  full_unpaid: "Voller Rückstand",
  partial: "Teilzahlung",
  rent_only_paid: "Nur Kaltmiete gezahlt",
  nk_only_paid: "Nur NK gezahlt",
  bka_open: "BKA-Nachzahlung offen",
  old_arrears_only: "Altrückstände",
  paid: "Alles bezahlt",
  cash_missing_full: "Kaution komplett offen",
  cash_missing_partial: "Kaution teilweise offen",
  cash_installment_overdue: "Kautionsrate überfällig",
  buergschaft_missing: "Bürgschaft offen",
  verpfaendung_missing: "Verpfändung offen",
};

const SCENARIO_COLOR = {
  full_unpaid: "bg-rose-100 text-rose-800 border-rose-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  rent_only_paid: "bg-sky-100 text-sky-800 border-sky-200",
  nk_only_paid: "bg-sky-100 text-sky-800 border-sky-200",
  bka_open: "bg-purple-100 text-purple-800 border-purple-200",
  old_arrears_only: "bg-orange-100 text-orange-800 border-orange-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cash_missing_full: "bg-rose-100 text-rose-800 border-rose-200",
  cash_missing_partial: "bg-amber-100 text-amber-800 border-amber-200",
  cash_installment_overdue: "bg-orange-100 text-orange-800 border-orange-200",
  buergschaft_missing: "bg-purple-100 text-purple-800 border-purple-200",
  verpfaendung_missing: "bg-purple-100 text-purple-800 border-purple-200",
};

export default function DunningTriggerDialog({ open, tenancyId, onClose, onCreated }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeRent, setIncludeRent] = useState(true);
  const [includeDeposit, setIncludeDeposit] = useState(true);
  const [forceLevel, setForceLevel] = useState("auto");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !tenancyId) return;
    setLoading(true);
    api.get(`/dunning/analyze/${tenancyId}`)
      .then(({ data }) => setAnalysis(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Analyse fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, [open, tenancyId]);

  const trigger = async () => {
    setBusy(true);
    try {
      const payload = {
        include_rent: includeRent,
        include_deposit: includeDeposit,
        force_level: forceLevel === "auto" ? null : Number(forceLevel),
      };
      const { data } = await api.post(`/dunning/run-tenancy/${tenancyId}`, payload);
      const created = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      if (created > 0) {
        toast.success(`${created} Vorschlag/Vorschläge in Freigabe-Queue angelegt` + (skipped ? ` · ${skipped} übersprungen` : ""));
        onCreated && onCreated();
        onClose();
      } else if (skipped > 0) {
        const reasons = data.skipped.map((s) => `${s.kind === "deposit" ? "Kaution" : "Miete"}: ${s.reason}`).join(" · ");
        toast.info(`Nichts angelegt — ${reasons}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setBusy(false);
    }
  };

  const rent = analysis?.rent;
  const deposit = analysis?.deposit;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dunning-trigger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={18} /> Zahlungsaufforderung erstellen
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-slate-500 text-sm">Analysiere Mietkonto …</div>}

        {!loading && analysis && (
          <div className="space-y-4 py-2">
            {/* Miete */}
            <div className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Receipt size={16} className="text-slate-600" />
                  <span className="font-semibold text-slate-900">Miete / NK / BKA</span>
                </div>
                <Switch checked={includeRent} onCheckedChange={setIncludeRent} data-testid="include-rent-switch" />
              </div>

              {rent?.scenario === "paid" ? (
                <div className="flex items-center gap-2 text-emerald-700 text-sm">
                  <CheckCircle size={16} weight="fill" /> Keine offenen Forderungen.
                </div>
              ) : (
                <>
                  <div className="mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${SCENARIO_COLOR[rent?.scenario] || ""}`}
                          data-testid="rent-scenario-badge">
                      {SCENARIO_LABEL[rent?.scenario] || rent?.scenario}
                    </span>
                    <p className="text-xs text-slate-600 mt-1">{rent?.scenario_label}</p>
                  </div>
                  <table className="text-xs w-full">
                    <tbody>
                      <tr><td className="py-0.5 text-slate-600">Aktuelle Periode:</td>
                          <td className="py-0.5 text-right font-mono">{rent?.current_period?.month}</td></tr>
                      <tr><td className="py-0.5 text-slate-600">Letzte Fälligkeit:</td>
                          <td className="py-0.5 text-right font-mono">{formatDate(rent?.letzte_faelligkeit)}</td></tr>
                      <tr><td className="py-0.5 text-slate-600">Geschuldet (Periode):</td>
                          <td className="py-0.5 text-right tabular-nums">{formatEUR(rent?.current_period?.soll_eur)}</td></tr>
                      <tr><td className="py-0.5 text-slate-600">Eingegangen (Periode):</td>
                          <td className="py-0.5 text-right tabular-nums text-emerald-700">{formatEUR(rent?.current_period?.ist_eur)}</td></tr>
                      <tr><td className="py-0.5 text-slate-600">Differenz (Periode):</td>
                          <td className="py-0.5 text-right tabular-nums font-semibold text-rose-700">
                            {formatEUR(rent?.current_period?.diff_eur)}</td></tr>
                      {rent?.old_arrears_eur > 0.01 && (
                        <tr><td className="py-0.5 text-slate-600">+ Altrückstände:</td>
                            <td className="py-0.5 text-right tabular-nums text-orange-700">
                              {formatEUR(rent?.old_arrears_eur)}</td></tr>
                      )}
                      <tr className="border-t border-slate-200"><td className="py-1 pt-1.5 font-semibold">Gesamt offen:</td>
                          <td className="py-1 pt-1.5 text-right tabular-nums font-bold text-rose-800">
                            {formatEUR(rent?.outstanding_total_eur)}</td></tr>
                    </tbody>
                  </table>
                </>
              )}
            </div>

            {/* Kaution */}
            {deposit && (
              <div className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <HandCoins size={16} className="text-slate-600" />
                    <span className="font-semibold text-slate-900">Kaution</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      {deposit.deposit_type === "cash" ? "Barkaution"
                       : deposit.deposit_type === "buergschaft" ? "Bürgschaft" : "Verpfändung"}
                    </span>
                  </div>
                  <Switch checked={includeDeposit} onCheckedChange={setIncludeDeposit} data-testid="include-deposit-switch" />
                </div>

                {deposit.scenario === "paid" ? (
                  <div className="flex items-center gap-2 text-emerald-700 text-sm">
                    <CheckCircle size={16} weight="fill" /> {deposit.scenario_label}
                  </div>
                ) : (
                  <>
                    <div className="mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${SCENARIO_COLOR[deposit.scenario] || ""}`}
                            data-testid="deposit-scenario-badge">
                        {SCENARIO_LABEL[deposit.scenario] || deposit.scenario}
                      </span>
                      <p className="text-xs text-slate-600 mt-1">{deposit.scenario_label}</p>
                    </div>
                    <table className="text-xs w-full">
                      <tbody>
                        <tr><td className="py-0.5 text-slate-600">Vereinbart:</td>
                            <td className="py-0.5 text-right tabular-nums">{formatEUR(deposit.deposit_soll_eur)}</td></tr>
                        <tr><td className="py-0.5 text-slate-600">Eingezahlt/Vorgelegt:</td>
                            <td className="py-0.5 text-right tabular-nums text-emerald-700">{formatEUR(deposit.deposit_ist_eur)}</td></tr>
                        <tr className="border-t border-slate-200"><td className="py-1 pt-1.5 font-semibold">Offen:</td>
                            <td className="py-1 pt-1.5 text-right tabular-nums font-bold text-rose-800">
                              {formatEUR(deposit.deposit_open_eur)}</td></tr>
                      </tbody>
                    </table>
                    {deposit.installment_status?.length > 0 && (
                      <div className="mt-2 text-[11px]">
                        <div className="text-slate-500 uppercase font-semibold mb-1">Ratenplan § 551 BGB</div>
                        <div className="grid grid-cols-3 gap-2">
                          {deposit.installment_status.map((r) => (
                            <div key={r.index} className={`border rounded p-1.5 text-center
                              ${r.status === "paid" ? "bg-emerald-50 border-emerald-200"
                                : r.status === "overdue" ? "bg-rose-50 border-rose-200"
                                : "bg-slate-50 border-slate-200"}`}>
                              <div className="font-mono">{r.index}. Rate</div>
                              <div className="text-slate-600">{formatDate(r.due_date)}</div>
                              <div className="tabular-nums font-semibold">{formatEUR(r.amount)}</div>
                              <div className={`text-[10px] uppercase font-semibold
                                ${r.status === "paid" ? "text-emerald-700"
                                  : r.status === "overdue" ? "text-rose-700" : "text-slate-500"}`}>
                                {r.status === "paid" ? "bezahlt" : r.status === "overdue" ? "überfällig" : "offen"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Stufe wählen */}
            <div className="border border-blue-200 bg-blue-50 rounded-md p-3">
              <Label className="text-xs uppercase tracking-wider font-semibold text-blue-900 mb-2 block">
                <Info size={12} className="inline mr-1" /> Stufe für die Miet-ZA
              </Label>
              <Select value={String(forceLevel)} onValueChange={setForceLevel}>
                <SelectTrigger className="bg-white" data-testid="force-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatisch (nach Einstellungen)</SelectItem>
                  <SelectItem value="1">Stufe 1 — Zahlungserinnerung</SelectItem>
                  <SelectItem value="2">Stufe 2 — 1. Zahlungsaufforderung</SelectItem>
                  <SelectItem value="3">Stufe 3 — 2. Zahlungsaufforderung</SelectItem>
                  <SelectItem value="4">Stufe 4 — Letzte Zahlungsaufforderung</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-blue-800 mt-2 flex items-start gap-1">
                <Warning size={12} className="mt-0.5 flex-shrink-0" />
                Der Vorschlag landet in der Freigabe-Queue und wird erst nach manueller Freigabe verschickt.
                Kaution-ZA hat keine Stufen — sie wird nach Szenario ausgewählt.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={trigger} disabled={busy || loading || (!includeRent && !includeDeposit)}
                  className="bg-slate-900 hover:bg-slate-800" data-testid="dunning-trigger-confirm">
            {busy ? "Erstelle …" : "Vorschlag(e) anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
